import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Skeleton as MuiSkeleton } from '@mui/material';
import { defaultNumberFormat } from '@cyberint/common-utils';
import { CustomTooltip } from '../../Poppers';

import * as S from './styled';

const DefaultLegend = ({
  payload,
  selectedIndex,
  partialSelectedIndex,
  hoverIndex,
  onMouseEnter,
  onMouseLeave,
  onClick,
  title,
  loading,
  legendWidth
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(hoverIndex);

  useEffect(() => {
    setHoveredIndex(hoverIndex);
  }, [hoverIndex]);

  const mouseEnter = (item, idx) => () => {
    onMouseEnter && onMouseLeave ? onMouseEnter(item, idx) : setHoveredIndex(idx);
  };

  const mouseLeave = (item, idx) => () => {
    onMouseEnter && onMouseLeave ? onMouseLeave(item, idx) : setHoveredIndex(-1);
  };

  const clickHandler = (item, idx) => () => {
    onClick?.(item, idx);
  };

  return (
    <S.LegendContainer>
      {title && <S.Title>{title}</S.Title>}
      <S.LegendWrapper>
        {payload.map((item, index) => {
          const isActive = Array.isArray(selectedIndex) ? selectedIndex.includes(index) : index === selectedIndex;
          const isPartial = Array.isArray(partialSelectedIndex)
            ? partialSelectedIndex.includes(index)
            : index === partialSelectedIndex;
          const tooltip =
            item.tooltip &&
            (typeof item.tooltip === 'string' ? <S.LegendToolTip>{item.tooltip}</S.LegendToolTip> : item.tooltip);

          return loading ? (
            <S.SkeletonWrapper key={item.name || index} $legendWidth={item?.legendWidth || legendWidth}>
              <MuiSkeleton variant="rounded" animation="wave" />
            </S.SkeletonWrapper>
          ) : (
            <CustomTooltip key={item.name || index} title={tooltip} placement="right" width="100%" height="80%">
              <S.Legend
                onMouseEnter={mouseEnter(item, index)}
                onMouseLeave={mouseLeave(item, index)}
                onClick={clickHandler(item, index)}
                $isDisabled={item?.payload?.isDisabled}
                $legendWidth={item?.legendWidth || legendWidth}
                isHover={index === hoveredIndex}
                $isPartial={isPartial}
                isActive={isActive}>
                {item?.payload?.icon || (item.color && <S.LegendColor color={item.color} />)}
                <S.LegendTextWrapper>
                  <S.LegendText isActive={isActive} isHover={index === hoveredIndex}>
                    {item.value}
                  </S.LegendText>
                  <S.LegendNumber isActive={isActive} isHover={index === hoveredIndex}>
                    {item?.payload?.prefix || null}
                    {defaultNumberFormat(item?.payload?.value)}
                    {item?.payload?.suffix || null}
                  </S.LegendNumber>
                </S.LegendTextWrapper>
              </S.Legend>
            </CustomTooltip>
          );
        })}
      </S.LegendWrapper>
    </S.LegendContainer>
  );
};

DefaultLegend.propTypes = {
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ).isRequired,
  selectedIndex: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.number), PropTypes.number]),
  partialSelectedIndex: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.number), PropTypes.number]),
  hoverIndex: PropTypes.number,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onClick: PropTypes.func,
  loading: PropTypes.bool,
  legendWidth: PropTypes.number,
  title: PropTypes.string
};

export default DefaultLegend;
